import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MaskedInput from 'react-maskedinput';
import { useTranslation } from 'react-i18next';
import { Grid, Card, CardContent, CardActions, Typography, Checkbox } from '@mui/material';
import { getCardTypeByNumber } from '../../helpers/accountData';
import commonStrings from '../../constants/commonStrings';
import MaskedCard from './MaskedCard';

const PaytraceCardInput = (props) => {
  const {
    card,
    cardErrors,
    onBack,
    updateCard = false,
    cardRef,
    expiryDateRef,
    cscCardRef,
    handleChangeCard,
    handleChangeCheckbox,
    showSaveCheckbox,
    isSaveChecked,
  } = props;
  const { t } = useTranslation();
  const [isAmericanExpress, setIsAmericanExpress] = React.useState(false);

  const handleChangeCardNumber = (e) => {
    setIsAmericanExpress(getCardTypeByNumber(e?.target?.value) === commonStrings.americanExpress);

    handleChangeCard(e);
  };

  return (
    <Card className={classNames(['cardData', 'customCardWraper', 'block'])}>
      <div className="Card_header">
        <Typography component="h2">Card Details</Typography>
        {typeof onBack === 'function' && (
          <a className="cardBackButton" onClick={(e) => onBack(e, t)}>
            back
          </a>
        )}
      </div>
      <CardContent ref={cardRef} className="cardContents">
        {cardErrors?.cardNumber ? (
          <label className={classNames(['MuiTypography-gutterBottom', cardErrors?.cardNumber ? 'errorCard' : ''])}>
            {t('cardNumberInvalid')}
          </label>
        ) : (
          <label className={classNames(['MuiTypography-gutterBottom', cardErrors?.cardNumber ? 'errorCard' : ''])}>
            {t('cardNumber')}
          </label>
        )}
        <MaskedInput
          type="text"
          value={card?.cardNumber}
          className="pt-encrypt"
          mask={isAmericanExpress ? '111111111111111' : '1111111111111111'}
          labels={t('cardNumber')}
          id="cardNumber"
          name="encrypted_number"
          size="19"
          onChange={(e) => handleChangeCardNumber(e)}
        />
        <Grid container item xs={12} justifyContent={'space-between'}>
          <Grid item md={6} xs={6}>
            {cardErrors?.cardExpiryDate ? (
              <label
                className={classNames(['MuiTypography-gutterBottom', cardErrors?.cardExpiryDate ? 'errorCard' : ''])}
              >
                {t('cardExpiryDateInvalid')}
              </label>
            ) : (
              <label
                className={classNames(['MuiTypography-gutterBottom', cardErrors?.cardExpiryDate ? 'errorCard' : ''])}
              >
                {t('cardExpiryDate')}
              </label>
            )}
            <MaskedInput
              mask="11/1111"
              value={card?.cardExpiryDate}
              name="cardExpiryDate"
              id="cardExpiryDate"
              placeholder="mm/yyyy"
              ref={expiryDateRef}
              onChange={(e) => handleChangeCard(e)}
            />
          </Grid>
          <Grid item md={6} xs={6}>
            {cardErrors?.cardCsc ? (
              <label className={classNames(['MuiTypography-gutterBottom', cardErrors?.cardCsc ? 'errorCard' : ''])}>
                {t('cardCscInvalid')}
              </label>
            ) : (
              <label className={classNames(['MuiTypography-gutterBottom', cardErrors?.cardCsc ? 'errorCard' : ''])}>
                {t('cardCsc')}
              </label>
            )}
            <MaskedInput
              type="text"
              value={card?.cardCsc}
              mask={isAmericanExpress ? '1111' : '111'}
              className="pt-encrypt"
              id="cardCsc"
              name="encrypted_csc"
              ref={cscCardRef}
              onChange={(e) => handleChangeCard(e)}
            />
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        {!updateCard && showSaveCheckbox && (
          <>
            <Checkbox
              id="customerToken"
              labels="creatNewUser"
              checked={isSaveChecked}
              onChange={(e) => {
                handleChangeCheckbox(e);
              }}
              color="primary"
              inputProps={{
                'aria-label': 'secondary checkbox',
              }}
            />
            <label>Save card for future payments</label>
          </>
        )}
        {updateCard && (
          <>
            <Checkbox
              id="customerTokenUpdate"
              labels="creatNewUser"
              checked={isSaveChecked}
              onChange={(e) => {
                handleChangeCheckbox(e);
              }}
              value={isSaveChecked}
              color="primary"
              inputProps={{
                'aria-label': 'secondary checkbox',
              }}
            />
            <label>Update card for future payments</label>
          </>
        )}
      </CardActions>
    </Card>
  );
};

PaytraceCardInput.propTypes = {
  card: PropTypes.object,
  cardErrors: PropTypes.object,
  onBack: PropTypes.func,
  updateCard: PropTypes.bool,
  handleChangeCard: PropTypes.func,
  handleChangeCheckbox: PropTypes.func,
  cardRef: PropTypes.any,
  cscCardRef: PropTypes.any,
  expiryDateRef: PropTypes.any,
  showSaveCheckbox: PropTypes.bool,
  isSaveChecked: PropTypes.bool,
};

const PaytraceCard = (props) => {
  const {
    card,
    cardErrors,
    onBack,
    updateCard = false,
    cardRef,
    expiryDateRef,
    cscCardRef,
    handleChangeCard,
    handleChangeCheckbox,
    displayCardDetails = { showCardInput: true, showSaveCheckbox: true },
    isSaveChecked,
  } = props;

  return displayCardDetails?.showCardInput ? (
    <PaytraceCardInput
      card={card}
      cardErrors={cardErrors}
      cardRef={cardRef}
      expiryDateRef={expiryDateRef}
      cscCardRef={cscCardRef}
      handleChangeCard={handleChangeCard}
      handleChangeCheckbox={handleChangeCheckbox}
      onBack={onBack}
      updateCard={updateCard}
      showSaveCheckbox={displayCardDetails.showSaveCheckbox}
      isSaveChecked={isSaveChecked}
    />
  ) : (
    <MaskedCard
      headerText={displayCardDetails?.headerText ?? 'Saved Card Details'}
      cardDetails={{
        accountType: displayCardDetails?.accountType,
        maskedCardNumber: displayCardDetails?.maskedCardNumber,
      }}
    />
  );
};

PaytraceCard.propTypes = {
  card: PropTypes.object,
  cardErrors: PropTypes.object,
  onBack: PropTypes.func,
  updateCard: PropTypes.bool,
  handleChangeCard: PropTypes.func,
  handleChangeCheckbox: PropTypes.func,
  isSaveChecked: PropTypes.bool,
  cardRef: PropTypes.any,
  cscCardRef: PropTypes.any,
  expiryDateRef: PropTypes.any,
  displayCardDetails: PropTypes.shape({
    showCardInput: PropTypes.bool,
    headerText: PropTypes.string,
    accountType: PropTypes.string,
    maskedCardNumber: PropTypes.string,
    showSaveCheckbox: PropTypes.bool,
  }),
};

export default PaytraceCard;
